'use client';

import React from 'react';
import Link from 'next/link';
import { StateMark } from '@/components/ui/HorizonMark';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  BarChart2,
  ChevronLeft,
  ChevronRight,
  Users,
  Binoculars,
  CalendarRange,
  Star,
  Flame
} from 'lucide-react';
import { TagMention } from '@/components/ui/TagMention';
import { MentionDedupeProvider } from '@/components/ui/MentionDedupe';
import { extractTwitterHandleFromHref } from '@/utils/markdown-utils';

export interface WeeklyStats {
  topStory?: string;
  biggestMover?: string;
  emergingTrend?: string;
  riskFactors?: string;
  [key: string]: any;
}

export interface WeeklyRecap {
  id: string;
  weekStart: string;
  weekEnd: string;
  title: string;
  summary: string;
  content: string;
  highlights: string[];
  weeklyStats?: WeeklyStats;
  dominantThemes?: string[];
  sentiment: 'bullish' | 'bearish' | 'neutral' | 'mixed';
  sourceCounts?: {
    dailyRecaps?: number;
    breakingNews?: number;
    activeHandles?: number;
  };
  generatedAt: string;
  publishedAt?: string;
  model?: string;
}

interface HandleMapping {
  [handle: string]: { slug: string; name: string };
}

interface WeeklyRecapContentProps {
  latest: WeeklyRecap | null;
  recent: WeeklyRecap[];
  handleMap?: HandleMapping;
}

// Parse @mentions and $TOKEN references to link through TagMention
function parseTextWithMentions(text: string): Array<{ type: 'text' | 'mention' | 'token'; value: string }> {
  if (!text) return [];
  const mentionRegex = /(@[a-zA-Z][a-zA-Z0-9_]*|\$[a-zA-Z][a-zA-Z0-9]*)/g;
  const segments: Array<{ type: 'text' | 'mention' | 'token'; value: string }> = [];
  let lastIndex = 0;
  let match;
  while ((match = mentionRegex.exec(text)) !== null) {
    if (match.index > lastIndex) segments.push({ type: 'text', value: text.substring(lastIndex, match.index) });
    const matched = match[0];
    segments.push(matched.startsWith('@')
      ? { type: 'mention', value: matched.substring(1) }
      : { type: 'token', value: matched.substring(1) });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) segments.push({ type: 'text', value: text.substring(lastIndex) });
  return segments;
}

function TextWithMentions({ text, handleMap }: { text: string; handleMap?: HandleMapping }) {
  const segments = parseTextWithMentions(text);
  return (
    <>
      {segments.map((seg, i) => {
        if (seg.type === 'mention' || seg.type === 'token') {
          const mapping = handleMap?.[seg.value.toLowerCase()];
          const prefix = seg.type === 'mention' ? '@' : '$';
          if (mapping) return <TagMention key={`m-${i}`} slug={mapping.slug} />;
          return <React.Fragment key={i}>{prefix}{seg.value}</React.Fragment>;
        }
        return <React.Fragment key={i}>{seg.value}</React.Fragment>;
      })}
    </>
  );
}

function sentimentIcon(sentiment: string, size = 'w-5 h-5') {
  switch (sentiment) {
    case 'bullish': return <TrendingUp className={`${size} text-signal-green`} />;
    case 'bearish': return <TrendingDown className={`${size} text-alert-red`} />;
    case 'mixed':   return <BarChart2 className={`${size} text-scout-gold`} />;
    default:        return <Minus className={`${size} text-parchment-dim`} />;
  }
}

function sentimentLabel(sentiment: string) {
  switch (sentiment) {
    case 'bullish': return { text: 'Bullish',  color: 'text-signal-green' };
    case 'bearish': return { text: 'Bearish',  color: 'text-alert-red' };
    case 'mixed':   return { text: 'Mixed',    color: 'text-scout-gold' };
    default:        return { text: 'Neutral',  color: 'text-parchment-dim' };
  }
}

// UTC-pinned throughout: start/end are date-only keys (YYYY-MM-DD) that new Date()
// parses as UTC midnight. Local-tz getters/formatting would render the previous day
// for viewers west of UTC — a wrong-date bug and a hydration mismatch vs the UTC-baked
// (ISR) server HTML. UTC getters + timeZone:'UTC' keep the range stable everywhere.
function formatRange(start: string, end: string) {
  const s = new Date(start), e = new Date(end);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', timeZone: 'UTC' };
  const sameYear = s.getUTCFullYear() === e.getUTCFullYear();
  const sameMonth = s.getUTCMonth() === e.getUTCMonth();
  if (sameMonth && sameYear) {
    return `${s.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' })} ${s.getUTCDate()}–${e.getUTCDate()}, ${s.getUTCFullYear()}`;
  }
  return `${s.toLocaleDateString('en-US', opts)} – ${e.toLocaleDateString('en-US', opts)}, ${e.getUTCFullYear()}`;
}

function formatShortRange(start: string, end: string) {
  const s = new Date(start), e = new Date(end);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', timeZone: 'UTC' };
  return `${s.toLocaleDateString('en-US', opts)} – ${e.toLocaleDateString('en-US', opts)}`;
}

export default function WeeklyRecapContent({ latest, recent, handleMap }: WeeklyRecapContentProps) {
  if (!latest) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center px-4">
          <StateMark size={96} className="mx-auto mb-6" />
          <h1 className="font-display text-3xl font-bold text-parchment mb-3">
            No Weekly Roundups Yet
          </h1>
          <p className="text-parchment-dim mb-8 max-w-md mx-auto">
            The first weekly roundup drops Sunday. Catch up on daily recaps while you wait.
          </p>
          <Link
            href="/daily"
            className="inline-flex items-center gap-2 px-5 py-2.5 border-[0.5px] border-scout-gold text-scout-gold rounded-lg hover:bg-scout-gold/10 transition-colors font-mono text-[11px] uppercase tracking-[0.14em]"
          >
            <ChevronLeft className="w-4 h-4" />
            Read Daily Recaps
          </Link>
        </div>
      </div>
    );
  }

  const markdownComponents = {
    p: ({ children, ...props }: any) => {
      const processChildren = (child: any): any => {
        if (typeof child === 'string') return <TextWithMentions text={child} handleMap={handleMap} />;
        if (Array.isArray(child)) return child.map((c, i) => <React.Fragment key={i}>{processChildren(c)}</React.Fragment>);
        if (child?.props?.children) return React.cloneElement(child, { ...child.props, children: processChildren(child.props.children) });
        return child;
      };
      return <p className="mb-6" {...props}>{processChildren(children)}</p>;
    },
    h3: ({ children, ...props }: any) => (
      <h3 className="font-display text-lg font-bold text-parchment mt-12 mb-4 pb-3 border-b-[0.5px] border-divider" {...props}>{children}</h3>
    ),
    h4: ({ children, ...props }: any) => (
      <h4 className="font-display text-base font-bold text-parchment mt-8 mb-3" {...props}>{children}</h4>
    ),
    ul: ({ children, ...props }: any) => (
      <ul className="my-6 space-y-3 pl-0 list-none" {...props}>{children}</ul>
    ),
    li: ({ children, ...props }: any) => (
      <li className="flex items-start gap-3 text-parchment/90" {...props}>
        <span className="w-1.5 h-1.5 rounded-full bg-scout-gold mt-2.5 flex-shrink-0" />
        <span>{children}</span>
      </li>
    ),
    blockquote: ({ children, ...props }: any) => (
      <blockquote className="my-8 rounded-r-lg border-l-4 border-scout-gold bg-surface py-4 pl-6 italic text-parchment-dim" {...props}>{children}</blockquote>
    ),
    a: ({ href, children, ...props }: any) => {
      // Inline twitter/x.com profile links bypass the @mention path. When the
      // handle maps to a known entity, render the rich chip (de-duped to first
      // mention); otherwise leave it a plain external link.
      const twitterHandle = extractTwitterHandleFromHref(href);
      const mapping = twitterHandle ? handleMap?.[twitterHandle] : undefined;
      if (mapping) {
        return <TagMention slug={mapping.slug} />;
      }
      return (
        <a href={href} className="text-scout-gold underline transition-colors hover:text-scout-gold/80" target={href?.startsWith('http') ? '_blank' : undefined} rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined} {...props}>{children}</a>
      );
    },
  };

  const stats = latest.weeklyStats || {};

  const sentiment = sentimentLabel(latest.sentiment);

  return (
    <div className="py-8 lg:py-10">
      <MentionDedupeProvider>
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <header className="mb-10">
          <div className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[11px] uppercase tracking-[0.14em] text-parchment-dim">
            <Link
              href="/weekly"
              className="inline-flex items-center gap-1.5 transition-colors hover:text-scout-gold"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              Latest Weekly
            </Link>
            <span>·</span>
            <span className="inline-flex items-center gap-1.5 text-scout-gold">
              <CalendarRange className="w-3.5 h-3.5" />
              Week in Review
            </span>
            <span>·</span>
            <span>{formatRange(latest.weekStart, latest.weekEnd)}</span>
            <span>·</span>
            <span className={`inline-flex items-center gap-1 ${sentiment.color}`}>
              {sentimentIcon(latest.sentiment, 'w-3.5 h-3.5')}
              {sentiment.text}
            </span>
          </div>

          <h1 className="mb-4 font-display text-3xl font-bold leading-[1.15] tracking-[-0.02em] text-parchment md:text-4xl lg:text-[2.75rem]">
            {latest.title}
          </h1>

          <p className="font-body text-lg leading-relaxed text-parchment-dim md:text-xl">
            <TextWithMentions text={latest.summary} handleMap={handleMap} />
          </p>
        </header>

        {/* Weekly Stats strip */}
        {(stats.topStory || stats.biggestMover || stats.emergingTrend) && (
          <section className="mb-10 grid grid-cols-1 gap-3 md:grid-cols-3">
            {stats.topStory && (
              <StatCard icon={<Star className="w-4 h-4" />} label="Top Story" value={stats.topStory} handleMap={handleMap} />
            )}
            {stats.biggestMover && (
              <StatCard icon={<TrendingUp className="w-4 h-4" />} label="Biggest Mover" value={stats.biggestMover} handleMap={handleMap} />
            )}
            {stats.emergingTrend && (
              <StatCard icon={<Flame className="w-4 h-4" />} label="Emerging Trend" value={stats.emergingTrend} handleMap={handleMap} />
            )}
          </section>
        )}

        {/* Dominant Themes pills */}
        {latest.dominantThemes && latest.dominantThemes.length > 0 && (
          <section className="mb-10 flex flex-wrap items-center gap-2">
            <span className="mr-1 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-parchment-dim">
              Week's Themes
            </span>
            {latest.dominantThemes.map((theme, i) => (
              <span key={i} className="rounded-full border-[0.5px] border-divider bg-surface px-3 py-1 text-xs font-medium text-parchment/90">
                {theme}
              </span>
            ))}
          </section>
        )}

        {/* Key Highlights */}
        {latest.highlights?.length > 0 && (
          <section className="mb-10 border-t-[0.5px] border-divider pt-8">
            <h2 className="mb-6 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-parchment-dim">
              Key Highlights
            </h2>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {latest.highlights.map((h, idx) => (
                <div key={idx} className="group flex items-start gap-4 rounded-xl border-[0.5px] border-divider bg-surface p-5 transition-colors hover:border-scout-gold/50">
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-scout-gold/10 font-mono text-sm font-bold text-scout-gold">
                    {idx + 1}
                  </span>
                  <p className="text-[15px] leading-relaxed text-parchment/90">
                    <TextWithMentions text={h} handleMap={handleMap} />
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Full Analysis */}
        <section className="border-t-[0.5px] border-divider pt-8">
          <h2 className="mb-8 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-parchment-dim">
            Full Weekly Analysis
          </h2>
          <div className="
            prose prose-invert prose-lg max-w-none break-words
            prose-p:text-parchment/90 prose-p:leading-[1.8] prose-p:mb-6 prose-p:font-body
            prose-strong:text-parchment prose-strong:font-semibold
            prose-h3:font-display prose-h3:text-xl prose-h3:font-bold prose-h3:mt-12 prose-h3:mb-4
            prose-h3:text-parchment
            prose-h3:pb-3 prose-h3:border-b-[0.5px] prose-h3:border-divider
            [&>p:first-of-type]:text-xl [&>p:first-of-type]:text-parchment
            [&>p:first-of-type]:leading-relaxed [&>p:first-of-type]:font-medium
          ">
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={markdownComponents}>
              {latest.content}
            </ReactMarkdown>
          </div>
        </section>

        {/* Sources & Meta */}
        <section className="mt-10 rounded-xl border-[0.5px] border-divider bg-surface px-5 py-4">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-[0.12em] text-parchment-dim">
            <span className="inline-flex items-center gap-2">
              <Binoculars className="w-4 h-4 text-scout-gold" />
              Powered by RECON Intel
            </span>
            <span className="inline-flex items-center gap-2">
              <CalendarRange className="w-4 h-4" />
              {latest.sourceCounts?.dailyRecaps || 0} daily recaps synthesized
            </span>
            <span className="inline-flex items-center gap-2">
              <Users className="w-4 h-4" />
              {latest.sourceCounts?.activeHandles || 0} ecosystem voices
            </span>
          </div>
        </section>

        {/* Previous Weeks */}
        {recent.length > 0 && (
          <section className="mt-10 border-t-[0.5px] border-divider pt-8">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-parchment-dim">
                Previous Weeks
              </h2>
              <Link href="/weekly" className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.14em] text-scout-gold transition-colors hover:text-scout-gold/80">
                View all <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {recent.slice(0, 3).map((r) => {
                const s = sentimentLabel(r.sentiment);
                return (
                  <Link
                    key={r.id}
                    href={`/weekly/${r.weekEnd}`}
                    className="group rounded-xl border-[0.5px] border-divider bg-surface p-5 transition-colors hover:border-scout-gold/50"
                  >
                    <div className="mb-3 flex items-center justify-between">
                      <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-parchment">
                        {formatShortRange(r.weekStart, r.weekEnd)}
                      </span>
                      <span className={`inline-flex items-center gap-1 ${s.color}`}>
                        {sentimentIcon(r.sentiment, 'w-3.5 h-3.5')}
                      </span>
                    </div>
                    <p className="line-clamp-3 text-sm text-parchment-dim transition-colors group-hover:text-parchment">
                      {r.title}
                    </p>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </div>
      </MentionDedupeProvider>
    </div>
  );
}

function StatCard({ icon, label, value, handleMap }: { icon: React.ReactNode; label: string; value: string; handleMap?: HandleMapping }) {
  return (
    <div className="rounded-xl border-[0.5px] border-divider bg-surface p-5">
      <div className="mb-2 flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-scout-gold">
        {icon}
        {label}
      </div>
      <p className="text-sm leading-relaxed text-parchment/90">
        <TextWithMentions text={value} handleMap={handleMap} />
      </p>
    </div>
  );
}
